import {Key, ReactNode} from "react";
import {To} from "react-router-dom";
import {ScrollingContext} from "../../components/SavedView/Table/impls";
import {OrderDirection} from "platformed-client/types";

export type SortOpts = {
  /** The default sort direction for this column. Default: "ascending". */
  defaultDirection?: OrderDirection;
};

export type GroupOpts<Item = any, Ctx = any, Value = any> = {
  /**
   * A function that extracts a unique key for the given extracted value.
   *
   * If the value is an object or map (for example, an owner), ensure that
   * values with the same key are identical, as the value will be passed to
   * `renderHeader` and it is not defined which item's value will be used.
   */
  extractUniqueKey: (value: Value, item: Item, ctx: Ctx) => Key | null | undefined;

  /**
   * A function to determine whether a header row should be rendered for this
   * group. If omitted, the row is always rendered.
   */
  shouldRenderHeaderRow?: (value: Value, ctx: Ctx) => boolean;

  /** A function to render the group header. */
  renderHeader: (value: Value, ctx: Ctx) => ReactNode;

  /**
   * If `true`, show the group label (as specified by `label` or
   * `overrideGroupAndSortLabel`) as a prefix in the group header, e.g.
   * "Owner: John Doe".
   *
   * Default: `true`
   */
  showPrefixInHeader?: boolean;

  /**
   * If provided, this column will only be selectable as a sub-group, and only when
   * the primary group matches the parent group column ID.
   *
   * Default: `undefined`.
   */
  parentGroupColumnId?: string;
};

/** A column definition. */
export type Column<Item = any, Ctx = any, Value = any> = {
  /** A label shown as the column header in the table, and also in the Display menu. */
  label: ReactNode;

  /** A function to extract the cell value from the row item. */
  extract: (item: Item, ctx: Ctx) => Value;

  /**
   * A function to render the cell contents. If omitted, the column is not
   * renderable and will not show up in the table or in the display menu's
   * "Columns" list.
   *
   * This is useful if you still want the table to be sortable or groupable by
   * this facet but do not want it showing up as a column.
   */
  render?: (value: Value, item: Item, ctx: Ctx, scrollingContext?: ScrollingContext) => ReactNode;

  /**
   * Compare two extracted values. This must be defined for the column to be
   * sortable or groupable.
   */
  compare?: (a: Value, b: Value) => number;

  /**
   * Is the table sortable by this column?
   *
   * - If `false`, the column is not sortable.
   * - If `true`, the column is sortable as long as `compare` is defined.
   * - If an object, same as `true` but with additional options.
   *
   * Default: `true`
   */
  sort?: boolean | SortOpts;

  /**
   * A function to determine whether the column should be hidden based on the
   * context. If omitted, the column is always visible unless not renderable or
   * hidden by the user.
   *
   * Hidden columns are not shown in the table or in the display menu's
   * "Columns" list or ordering / sorting selectors.
   *
   * If you just want a column not to show up in the table but still be usable
   * for grouping and sorting, you should instead omit the `render` function.
   */
  hide?: (ctx: Ctx) => boolean;

  /**
   * Is the table groupable by this column?
   *
   * - If `true`, the column is groupable as long as `compare` is defined.
   *
   * Default: `false`
   */
  group?: GroupOpts<Item, Ctx, Value>;

  /**
   * If `true`, a click in this cell neither follows the row's link nor picks
   * the row, so a control the cell holds answers its own click.
   */
  disableRowInteraction?: boolean | ((value: Value, item: Item, ctx: Ctx) => boolean);

  /** If `true`, this column cannot be hidden by the user. */
  forceVisible?: boolean;

  /**
   * Fractional width of this column. Defaults to `1`. The total width is
   * then calculated as:
   *
   * `w = ((fr / (sum of visible column frs)) * 100)%`
   */
  fractionalWidth?: number;

  /** Where in the column its header and cells sit. Default: `"start"`. */
  align?: "start" | "center" | "end";

  /**
   * Override the column label as shown in the Display menu's "Grouping" and
   * "Ordering" columns. If omitted, the `label` is used.
   */
  overrideGroupAndSortLabel?: string;

  /**
   * Optional backend fields associated with this column. Used to translate
   * frontend sorting/grouping to backend ordering queries.
   */
  backendFields?: string[];
};

/** A record of many column definitions. */
export type Columns<Item = any, Ctx = any> = Record<string, Column<Item, Ctx, any>>;

export type TableConfig<Item = any, ItemKey extends Key = Key, Ctx = any, Cs extends Columns<Item, Ctx> = Columns> = {
  /**
   * A unique key for this table. This is used when generating storage keys for
   * persisting the table state.
   */
  tableUniqueKey: string;

  /**
   * Enable row selection. A table with this enabled will show selection boxes
   * next to each row. Selection state is managed by the `useTableState` hook
   * and can be controlled or uncontrolled. Default: `false`.
   *
   * May also be a function of the table context, to enable/disable selection
   * dynamically (e.g. to disable selection when the page is locked).
   */
  enableRowSelection?: boolean | ((ctx: Ctx) => boolean);

  /** Enable sub-grouping. Default: `false`. */
  enableSubGrouping?: boolean;

  /**
   * A function to extract a unique key from each item. This is used to identify
   * each item during bulk select, and also as the `key` prop when internally
   * rendering table rows.
   */
  extractUniqueKey: (item: Item) => ItemKey;

  /**
   * If provided, table rows will navigate to the returned link when clicked on.
   *
   * This can be disabled on a per-column basis using `disableRowInteraction`.
   */
  getRowLink?: (item: Item, ctx: Ctx) => To | undefined;

  /**
   * If provided, clicking a table row calls this: the row takes a decision in
   * place. `rowInteraction` settles how this and `getRowLink` combine. The
   * selection checkbox cell and a column that disables row interaction take no
   * part.
   */
  onRowClick?: (item: Item, ctx: Ctx) => void;

  /**
   * Given that `rowSelection` is enabled:
   *
   * If provided, this function will be used to determine whether a row is
   * selectable. If omitted, all rows are selectable.
   */
  getRowIsSelectable?: (item: Item, ctx: Ctx) => boolean;

  /**
   * If provided, the row this returns `true` for is drawn as active: the one
   * row a keyboard has singled out. Distinct from selection, which is a set
   * and is drawn with checkboxes.
   */
  getRowIsActive?: (item: Item, ctx: Ctx) => boolean;

  /**
   * If provided, the row this returns `true` for carries `aria-selected`: what
   * a combobox's `aria-activedescendant` points at is announced as selected.
   * Distinct from checkbox selection.
   */
  getRowIsSelected?: (item: Item, ctx: Ctx) => boolean;

  /**
   * If provided, the DOM id of the row element. A control outside the table
   * that names one of its rows — `aria-activedescendant` on a combobox whose
   * popup the table is — needs a handle on the row, which only the table can
   * mint.
   */
  getRowId?: (item: Item, ctx: Ctx) => string | undefined;

  /**
   * If provided, the row this returns `true` for is listed but is not a legal
   * target of the interaction the table offers, and carries `aria-disabled`.
   * The row keeps its place in the list and stays reachable, so whatever it
   * says about why it was refused can still be read.
   */
  getRowIsDisabled?: (item: Item, ctx: Ctx) => boolean;

  /** Column definitions. */
  columns: Cs;
};

export type ExtractTableItem<C extends TableConfig> = C extends TableConfig<infer Item> ? Item : never;
export type ExtractTableItemKey<C extends TableConfig> = C extends TableConfig<any, infer ItemKey> ? ItemKey : never;
export type ExtractTableContext<C extends TableConfig> = C extends TableConfig<any, any, infer Ctx> ? Ctx : never;
export type ExtractTableColumns<C extends TableConfig> = C extends TableConfig<any, any, any, infer Cs> ? Cs : never;

export type ExtractColumnItem<Col extends Column> = Col extends Column<infer Item> ? Item : never;
export type ExtractColumnContext<Col extends Column> = Col extends Column<any, infer Ctx> ? Ctx : never;
export type ExtractColumnValue<Col extends Column> = Col extends Column<any, any, infer Value> ? Value : never;

export type ColumnId<C extends TableConfig> = keyof C["columns"] & string;

/** Create a table configuration. This function requires two generic parameters:
 * - `Item`: the type of the items in the table that gets passed to the row
 * functions (`extract`, `sort`, `render`, etc.). There is one row per item.
 * - `Ctx`: an optional arbitrary context object that gets passed to the column
 * functions to allow them to communicate with things outside the table. For
 * example, you could pass in a search query to highlight cell text, or callback
 * functions to perform actions.
 */
export function createTableConfig<Item, Ctx = null>() {
  return <ItemKey extends Key, Cs extends Columns<Item, Ctx>>(
    config: TableConfig<Item, ItemKey, Ctx, Cs>,
  ): TableConfig<Item, ItemKey, Ctx, Cs> => config;
}

/** A helper function to create a table context. */
export function createTableContext<C extends TableConfig>(
  _config: C,
  ctx: ExtractTableContext<C>,
): ExtractTableContext<C> {
  return ctx;
}

/** A helper function to create a table column. */
export function createColumn<Item, Ctx, Value>(column: Column<Item, Ctx, Value>): Column<Item, Ctx, Value> {
  return column;
}
